import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Plus, Trash } from "@phosphor-icons/react";
import { BRAND } from "../config/brand";
import MoneyInput from "../components/MoneyInput";
import BrandPicker from "../components/BrandPicker";

const DEFAULT_WALLETS = [];

export default function Onboarding() {
    const { user, setUser, selectAgency } = useAuth();
    const [step, setStep] = useState(0);
    const [company, setCompany] = useState(user?.name ? `${user.name} Agency` : "");
    const [agency, setAgency] = useState({ name: "", address: "", brand_concessionario: null });
    const [wallets, setWallets] = useState(DEFAULT_WALLETS);
    const [providers, setProviders] = useState([
        { name: "SNAI", fido_limit: "" },
    ]);
    const [operator, setOperator] = useState({
        email: "",
        name: "",
        password: "",
    });
    const [saving, setSaving] = useState(false);
    const nav = useNavigate();

    const next = () => setStep((s) => s + 1);
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const submit = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/onboarding", {
                company_name: company,
                agency_name: agency.name,
                agency_address: agency.address,
                agency_brand_concessionario: agency.brand_concessionario || null,
                wallets: wallets.map(({ _key, ...w }) => ({
                    ...w,
                    initial_balance: parseFloat(w.initial_balance) || 0,
                })),
                providers: providers.map(({ _key, ...p }) => ({
                    ...p,
                    fido_limit: parseFloat(p.fido_limit) || 0,
                })),
                operator: operator.email ? operator : null,
            });
            selectAgency(data.agency_id);
            const me = await api.get("/auth/me");
            setUser(me.data);
            toast.success("Onboarding completato", {
                description: "Ora imposta i saldi di apertura della tua agenzia (puoi anche saltare e farlo dopo)",
            });
            nav("/settings/avanzate?onboarding=1");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const steps = ["Azienda", "Agenzia", "Wallet", "Fornitori", "Operatore"];

    return (
        <div
            className="min-h-screen grain"
            style={{
                backgroundImage:
                    "url(https://static.prod-images.emergentagent.com/jobs/93585fcd-1f53-4bac-bdc5-f9a2ce2b3880/images/6cab041efcf873e16670f2d51be5c9f0ee227da21b289cbf6aa2c870c8117381.png)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
            }}
        >
            <div className="min-h-screen bg-white/80 backdrop-blur-xl">
                <div className="max-w-3xl mx-auto px-6 py-12">
                    <div className="flex items-center mb-16">
                        <img
                            src={BRAND.logo}
                            alt={BRAND.name}
                            className="h-16 w-auto"
                            data-testid="onboarding-brand-logo"
                        />
                    </div>

                    <div className="overline mb-3">
                        Step {step + 1} di {steps.length}
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter mb-2">
                        {steps[step]}.
                    </h1>
                    <div className="flex gap-1 mb-12 mt-6">
                        {steps.map((s, i) => (
                            <div
                                key={s}
                                className={`h-1 flex-1 ${i <= step ? "bg-ink" : "bg-zinc-200"}`}
                                style={i <= step ? { backgroundColor: "#09090b" } : {}}
                            />
                        ))}
                    </div>

                    {step === 0 && (
                        <div>
                            <label className="block mb-8">
                                <div className="overline mb-2">Nome azienda</div>
                                <input
                                    className="inp-huge"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                    placeholder="Es: Agenzie Scommesse Bianchi SRL"
                                    data-testid="onboarding-company-input"
                                />
                            </label>
                            <p className="text-sm text-zinc-600 mb-8">
                                L'azienda è il contenitore di tutte le agenzie che gestisci.
                            </p>
                        </div>
                    )}

                    {step === 1 && (
                        <div>
                            <label className="block mb-8">
                                <div className="overline mb-2">Nome agenzia</div>
                                <input
                                    className="inp-huge"
                                    value={agency.name}
                                    onChange={(e) => setAgency({ ...agency, name: e.target.value })}
                                    placeholder="Es: Agenzia Milano Centro"
                                    data-testid="onboarding-agency-name"
                                />
                            </label>
                            <label className="block mb-8">
                                <div className="overline mb-2">Brand concessionario</div>
                                <BrandPicker
                                    value={agency.brand_concessionario}
                                    onChange={(id) => setAgency({ ...agency, brand_concessionario: id === "custom" ? null : id })}
                                    testId="onboarding-brand-picker"
                                />
                                <div className="text-xs text-zinc-500 mt-1">
                                    Opzionale. Identifica la rete del concessionario di
                                    riferimento (SNAI, Sisal, ecc.). Puoi cambiarlo dopo.
                                </div>
                            </label>
                            <label className="block mb-8">
                                <div className="overline mb-2">Indirizzo (opzionale)</div>
                                <input
                                    className="inp"
                                    value={agency.address}
                                    onChange={(e) => setAgency({ ...agency, address: e.target.value })}
                                    data-testid="onboarding-agency-address"
                                />
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <p className="text-sm text-zinc-600 mb-6">
                                Configura i wallet iniziali. Potrai aggiungerne altri in
                                seguito.
                            </p>
                            <div className="border border-zinc-200 bg-white">
                                {wallets.map((w, i) => (
                                    <div key={w._key || `w-${i}`} className="grid grid-cols-12 gap-2 p-3 border-b border-zinc-100">
                                        <input
                                            className="inp col-span-5"
                                            value={w.name}
                                            onChange={(e) => {
                                                const c = [...wallets];
                                                c[i].name = e.target.value;
                                                setWallets(c);
                                            }}
                                            placeholder="Nome"
                                            data-testid={`wallet-name-${i}`}
                                        />
                                        <select
                                            className="inp col-span-4"
                                            value={w.type}
                                            onChange={(e) => {
                                                const c = [...wallets];
                                                c[i].type = e.target.value;
                                                setWallets(c);
                                            }}
                                            data-testid={`wallet-type-${i}`}
                                        >
                                            <option value="cassa">Cassa</option>
                                            <option value="cassaforte">Cassaforte</option>
                                            <option value="banca">Banca</option>
                                            <option value="generico">Generico</option>
                                        </select>
                                        <MoneyInput
                                            className="inp col-span-2 tabular-nums"
                                            value={w.initial_balance}
                                            onChange={(v) => {
                                                const c = [...wallets];
                                                c[i].initial_balance = v;
                                                setWallets(c);
                                            }}
                                            placeholder="0,00"
                                            data-testid={`wallet-balance-${i}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setWallets(wallets.filter((_, idx) => idx !== i))
                                            }
                                            className="col-span-1 flex items-center justify-center text-zinc-400 hover:text-red-600"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setWallets([
                                            ...wallets,
                                            { _key: `w-${Date.now()}-${Math.random()}`, name: "", type: "generico", initial_balance: "" },
                                        ])
                                    }
                                    className="w-full flex items-center justify-center gap-2 p-3 text-sm font-bold uppercase tracking-wider text-zinc-600 hover:text-ink hover:bg-zinc-50"
                                    data-testid="add-wallet-row"
                                >
                                    <Plus size={14} weight="bold" /> Aggiungi wallet
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <p className="text-sm text-zinc-600 mb-6">
                                Aggiungi i fornitori di scommesse (SNAI, Eurobet, ecc.) con
                                il fido iniziale.
                            </p>
                            <div className="border border-zinc-200 bg-white">
                                {providers.map((p, i) => (
                                    <div key={p._key || `p-${i}`} className="grid grid-cols-12 gap-2 p-3 border-b border-zinc-100">
                                        <input
                                            className="inp col-span-7"
                                            value={p.name}
                                            onChange={(e) => {
                                                const c = [...providers];
                                                c[i].name = e.target.value;
                                                setProviders(c);
                                            }}
                                            placeholder="Es: SNAI"
                                            data-testid={`provider-name-${i}`}
                                        />
                                        <MoneyInput
                                            className="inp col-span-4 tabular-nums"
                                            value={p.fido_limit}
                                            onChange={(v) => {
                                                const c = [...providers];
                                                c[i].fido_limit = v;
                                                setProviders(c);
                                            }}
                                            placeholder="Fido €"
                                            data-testid={`provider-fido-${i}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setProviders(providers.filter((_, idx) => idx !== i))}
                                            className="col-span-1 flex items-center justify-center text-zinc-400 hover:text-red-600"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setProviders([...providers, { _key: `p-${Date.now()}-${Math.random()}`, name: "", fido_limit: "" }])
                                    }
                                    className="w-full flex items-center justify-center gap-2 p-3 text-sm font-bold uppercase tracking-wider text-zinc-600 hover:text-ink hover:bg-zinc-50"
                                    data-testid="add-provider-row"
                                >
                                    <Plus size={14} weight="bold" /> Aggiungi fornitore
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <p className="text-sm text-zinc-600 mb-6">
                                (Opzionale) Crea un account operatore che potrà registrare
                                transazioni in questa agenzia.
                            </p>
                            <div className="mb-6 p-4 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                                <strong>Prossimo passo:</strong> dopo aver salvato verrai
                                portato alla pagina <strong>Apertura saldi</strong> per
                                impostare la situazione iniziale (fondi, residui fornitori,
                                sospesi clienti, utili accantonati). Puoi anche saltarla
                                ora e completarla dopo — troverai un banner sulla
                                dashboard.
                            </div>
                            <label className="block mb-4">
                                <div className="overline mb-2">Nome operatore</div>
                                <input
                                    className="inp"
                                    value={operator.name}
                                    onChange={(e) => setOperator({ ...operator, name: e.target.value })}
                                    data-testid="operator-name"
                                />
                            </label>
                            <label className="block mb-4">
                                <div className="overline mb-2">Email</div>
                                <input
                                    type="email"
                                    className="inp"
                                    value={operator.email}
                                    onChange={(e) => setOperator({ ...operator, email: e.target.value })}
                                    data-testid="operator-email"
                                />
                            </label>
                            <label className="block mb-4">
                                <div className="overline mb-2">Password</div>
                                <input
                                    type="password"
                                    className="inp"
                                    value={operator.password}
                                    onChange={(e) => setOperator({ ...operator, password: e.target.value })}
                                    data-testid="operator-password"
                                />
                            </label>
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-12 pt-8 border-t border-zinc-200">
                        <button
                            onClick={prev}
                            disabled={step === 0}
                            className="btn-secondary"
                            data-testid="onboarding-prev"
                        >
                            Indietro
                        </button>
                        {step < steps.length - 1 ? (
                            <button
                                onClick={next}
                                disabled={
                                    (step === 0 && !company) ||
                                    (step === 1 && !agency.name)
                                }
                                className="btn-primary flex items-center gap-2"
                                data-testid="onboarding-next"
                            >
                                Avanti <ArrowRight size={16} weight="bold" />
                            </button>
                        ) : (
                            <button
                                onClick={submit}
                                disabled={saving}
                                className="btn-primary"
                                data-testid="onboarding-finish"
                            >
                                {saving ? "Salvataggio…" : "Completa setup"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
